import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { VisibilityLevel } from '@anidex/shared/economy';
import type { SitePalettes } from '@anidex/shared/premium';
import type { NotificationPrefKey } from '@anidex/shared/social';
import type {
  CompareSummaryDTO,
  OwnedCardDTO,
  Page,
  ProfileSettingsDTO,
  PublicProfileDTO,
  UserDTO,
} from '@anidex/shared/types';
import { CARD_PAGE_SIZE, pageCursor } from '../../hooks/useListPage';
import { api, queryString } from '../api';
import { hasListIntent } from '../list-intent';
import type { SessionPayload } from './account';
import { invalidateKeys } from './cache';
import type { CollectionFilters } from './filters';
import { keys } from './keys';

export function usePublicProfile(username: string | undefined) {
  return useQuery({
    queryKey: keys.publicProfile(username ?? ''),
    queryFn: () => api<PublicProfileDTO>(`/users/${encodeURIComponent(username!)}`),
    enabled: Boolean(username),
  });
}

export function usePublicCollection(username: string | undefined, filters: CollectionFilters, enabled = true) {
  const page = filters.page ?? 0;
  const limit = filters.limit ?? CARD_PAGE_SIZE;
  const intent = hasListIntent(filters);
  return useQuery({
    queryKey: keys.publicCollection(username ?? '', filters),
    queryFn: () =>
      api<Page<OwnedCardDTO>>(
        `/users/${encodeURIComponent(username!)}/collection${queryString({
          search: filters.search,
          rarity: filters.rarity,
          nametag: filters.nametag,
          animeId: filters.animeId,
          favorites: filters.favorites ? '1' : undefined,
          sort: filters.sort,
          limit,
          cursor: pageCursor(page, limit),
        })}`,
      ),
    placeholderData: intent ? keepPreviousData : undefined,
    enabled: Boolean(username) && enabled && intent,
  });
}

export function useCompare(username: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.compare(username ?? ''),
    queryFn: () => api<CompareSummaryDTO>(`/users/${encodeURIComponent(username!)}/compare`),
    enabled: Boolean(username) && enabled,
  });
}

export function useCompareCards(
  username: string | undefined,
  view: 'theirs' | 'mine' | 'common',
  enabled = true,
  page = 0,
) {
  const limit = CARD_PAGE_SIZE;
  return useQuery({
    queryKey: keys.compareCards(username ?? '', view, page),
    queryFn: () =>
      api<Page<OwnedCardDTO>>(
        `/users/${encodeURIComponent(username!)}/compare/cards${queryString({
          view,
          cursor: pageCursor(page, limit),
          limit,
        })}`,
      ),
    placeholderData: keepPreviousData,
    enabled: Boolean(username) && enabled,
  });
}

export function useSettings() {
  return useQuery({
    queryKey: keys.settings,
    queryFn: () => api<ProfileSettingsDTO>('/me/settings'),
  });
}

export function useSetAvatar() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (avatarCharacterId: number | null) => api<{ user: UserDTO }>('/me', { method: 'PATCH', body: { avatarCharacterId } }),
    onSuccess: (result) => {
      client.setQueryData<SessionPayload>(keys.session, (old) => ({ user: result.user, staff: old?.staff ?? null }));
      void client.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useSetSitePalette() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (palettes: SitePalettes | null) => api<{ user: UserDTO }>('/me/site-palette', { method: 'PUT', body: { palettes } }),
    onSuccess: (result) => {
      client.setQueryData<SessionPayload>(keys.session, (old) => ({ user: result.user, staff: old?.staff ?? null }));
      void client.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useUpdateSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      profileVisibility?: VisibilityLevel;
      collectionVisibility?: VisibilityLevel;
      tradeVisibility?: VisibilityLevel;
      showAnima?: boolean;
      showPresence?: boolean;
      allowFriendRequests?: boolean;
      notificationPrefs?: Partial<Record<NotificationPrefKey, boolean>>;
    }) => api<ProfileSettingsDTO>('/me/settings', { method: 'PATCH', body: input }),
    onSuccess: (settings) => {
      client.setQueryData(keys.settings, settings);
    },
  });
}

export function useUpdateShowcase() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (characterIds: number[]) => api<{ ok: boolean }>('/me/showcase', { method: 'PUT', body: { characterIds } }),
    onSuccess: () => {
      invalidateKeys(client, [keys.me, ['public_profile']]);
    },
  });
}

export function useUpdateProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      bannerAnimeId?: number | null;
      titleKey?: string | null;
      avatarCharacterId?: number | null;
      frameKey?: string | null;
      avatarFxKey?: string | null;
      bannerFxKey?: string | null;
      badgeKeys?: string[];
    }) => api<{ user: UserDTO | null }>('/me', { method: 'PATCH', body: input }),
    onSuccess: (result) => {
      if (result.user) {
        client.setQueryData<SessionPayload>(keys.session, (old) => ({ user: result.user, staff: old?.staff ?? null }));
      }
      invalidateKeys(client, [keys.me, ['public_profile']]);
    },
  });
}
